import { useState, useCallback, MutableRefObject } from 'react';
import { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { Template, TemplateSection, DragData } from '../../_types';
import { Field } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { isSectionLockedByParent } from '../../_utils/sectionComparison';
import { getRemovableChildFieldsInLockedSections } from '../../_utils/templateValidation';

export const useDragAndDrop = (
  templateEditable: Template,
  setTemplateEditable: (template: Template) => void,
  trackActivity: () => void,
  triggerAutoSave: (template: Template, note: string) => void,
  isReorderMode: boolean,
  addFieldToSection: (field: Field, sectionId: number, sectionGuid?: string) => Promise<void>,
  addFieldToSectionAtIndex: (
    field: Field,
    sectionId: number,
    insertIndex: number,
    sectionGuid?: string
  ) => Promise<void>,
  setOpenFieldOptionsIdState: (id: string | null) => void,
  selectedSectionRef: MutableRefObject<TemplateSection | null>,
  setSelectedSectionForEditing: (section: TemplateSection) => void,
  parentTemplate?: Template | null
) => {
  // Helper to find section by id or guid (for sections with id: 0)
  const findSectionByIdOrGuid = useCallback(
    (sectionId: number, sectionGuid?: string) => {
      if (sectionId === 0 && sectionGuid) {
        return templateEditable.templateSections.find((s) => s.sectionGuid === sectionGuid);
      }
      return templateEditable.templateSections.find((s) => s.id === sectionId);
    },
    [templateEditable]
  );

  // Drag state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragData, setDragData] = useState<DragData | null>(null);
  const [isOverDropZone, setIsOverDropZone] = useState<boolean>(false);

  // Drag operations
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);

    // Store drag data based on the dragged item
    const data = active.data.current as DragData;
    setDragData(data);

    // Close any open field options panel when starting a drag
    setOpenFieldOptionsIdState(null);
  }, []);

  // Handle field insertion at specific index (for repositioning existing fields)
  const handleFieldInsertAtIndex = useCallback(
    async (
      activeId: string,
      targetSectionId: number,
      insertIndex: number,
      dragData: DragData,
      targetSectionGuid?: string
    ) => {
      // Track user activity
      trackActivity();

      // Convert string ID to number for comparison
      const activeFieldId = Number(activeId);

      // Find target section using guid if id is 0
      const targetSection =
        targetSectionId === 0 && targetSectionGuid
          ? templateEditable.templateSections.find((s) => s.sectionGuid === targetSectionGuid)
          : templateEditable.templateSections.find((s) => s.id === targetSectionId);

      if (!targetSection) {
        console.error('Target section not found for field move', { targetSectionId, targetSectionGuid });
        return;
      }

      // Prevent moving fields from locked sections to other sections
      if (dragData.sourceSectionId !== targetSection.id) {
        const sourceSection = templateEditable.templateSections.find((s) => s.id === dragData.sourceSectionId);
        if (sourceSection && isSectionLockedByParent(sourceSection, parentTemplate)) {
          const removableChildFieldIds = getRemovableChildFieldsInLockedSections(templateEditable);
          const isRemovableChildField = removableChildFieldIds.has(dragData.templateField?.id || 0);
          
          if (!isRemovableChildField) {
            console.warn('Cannot move fields from locked sections to other sections');
            return; // Only prevent if NOT a removable child field
          }
        }
      }

      let updatedTemplate: Template;

      // Create updated template - use guid for comparison when id is 0
      // If moving within the same section
      if (targetSection.id === dragData.sourceSectionId) {
        updatedTemplate = {
          ...templateEditable,
          templateSections: templateEditable.templateSections.map((section) => {
            const isTargetSection =
              targetSectionId === 0 && targetSectionGuid
                ? section.sectionGuid === targetSectionGuid
                : section.id === targetSectionId;

            if (isTargetSection) {
              const oldIndex = section.sectionFields.findIndex((f) => f.id === activeFieldId);
              if (oldIndex === -1) return section;

              // Adjust insert index if moving item down in the same section
              const adjustedIndex = oldIndex < insertIndex ? insertIndex - 1 : insertIndex;

              const newFields = [...section.sectionFields];
              const [movedField] = newFields.splice(oldIndex, 1);
              newFields.splice(adjustedIndex, 0, movedField);

              // Update order numbers
              return {
                ...section,
                sectionFields: newFields.map((field, index) => ({
                  ...field,
                  order: index + 1,
                })),
              };
            }
            return section;
          }),
        };
      } else {
        // Moving between different sections
        // First, find the field to move
        const sourceSection = templateEditable.templateSections.find((s) => s.id === dragData.sourceSectionId);
        const fieldToMove = sourceSection?.sectionFields.find((f) => f.id === activeFieldId);

        if (!fieldToMove || !sourceSection) return;

        updatedTemplate = {
          ...templateEditable,
          templateSections: templateEditable.templateSections.map((section) => {
            if (section.id === dragData.sourceSectionId) {
              // Remove field from source section
              const newFields = section.sectionFields.filter((f) => f.id !== activeFieldId);
              return {
                ...section,
                sectionFields: newFields.map((field, index) => ({
                  ...field,
                  order: index + 1,
                })),
              };
            } else {
              // Use guid for comparison when id is 0
              const isTargetSection =
                targetSectionId === 0 && targetSectionGuid
                  ? section.sectionGuid === targetSectionGuid
                  : section.id === targetSectionId;

              if (isTargetSection) {
                // Add field to target section at specified index
                const newFields = [...section.sectionFields];
                newFields.splice(insertIndex, 0, fieldToMove);

                return {
                  ...section,
                  sectionFields: newFields.map((field, index) => ({
                    ...field,
                    order: index + 1,
                  })),
                };
              }
            }
            return section;
          }),
        };
      }

      // If a field was moved into a section that uses it as a visibility condition,
      // a section cannot be controlled by a field inside itself — reset the toggle.
      const templateWithVisibilityReset: Template = {
        ...updatedTemplate,
        templateSections: updatedTemplate.templateSections.map((section) => {
          const isTargetSection =
            targetSectionId === 0 && targetSectionGuid
              ? section.sectionGuid === targetSectionGuid
              : section.id === targetSectionId;

          if (
            isTargetSection &&
            section.isConditionallyVisible &&
            section.visibilityConditions?.some((condition) => condition.sectionFieldId === activeFieldId)
          ) {
            return {
              ...section,
              isConditionallyVisible: false,
              effectiveIsConditionallyVisible: false,
              visibilityConditions: [],
            };
          }
          return section;
        }),
      };

      // If a section panel is open, sync it to the updated template state
      const currentSelectedSection = selectedSectionRef.current;
      if (currentSelectedSection) {
        const freshSection =
          currentSelectedSection.id !== 0
            ? templateWithVisibilityReset.templateSections.find((s) => s.id === currentSelectedSection.id)
            : templateWithVisibilityReset.templateSections.find(
                (s) => s.sectionGuid === currentSelectedSection.sectionGuid
              );
        if (freshSection) {
          setSelectedSectionForEditing(freshSection);
        }
      }

      // Update UI immediately (optimistic)
      setTemplateEditable(templateWithVisibilityReset);

      // Trigger auto-save in background
      triggerAutoSave(templateWithVisibilityReset, 'Moved field between sections');
    },
    [templateEditable, trackActivity, triggerAutoSave, parentTemplate]
  );

  // Handle field reordering within sections
  const handleFieldReorder = useCallback(
    async (activeId: string, overId: string, dragData: DragData) => {
      if (activeId === overId) return;

      // Track user activity
      trackActivity();

      // Convert string IDs to numbers for comparison
      const activeFieldId = Number(activeId);
      const overFieldId = Number(overId);

      let updatedTemplate: Template;

      // Create updated template
      updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          if (section.id === dragData.sourceSectionId) {
            const oldIndex = section.sectionFields.findIndex((f) => f.id === activeFieldId);
            const newIndex = section.sectionFields.findIndex((f) => f.id === overFieldId);

            // If either field is not found, return unchanged
            if (oldIndex === -1 || newIndex === -1) return section;

            // Simple array reordering
            const newFields = [...section.sectionFields];
            const [movedField] = newFields.splice(oldIndex, 1);
            newFields.splice(newIndex, 0, movedField);

            // Update order numbers
            return {
              ...section,
              sectionFields: newFields.map((field, index) => ({
                ...field,
                order: index + 1,
              })),
            };
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Reordered fields within section');
    },
    [templateEditable, trackActivity, triggerAutoSave]
  );

  // Handle section reordering
  const handleSectionReorder = useCallback(
    (activeId: string, overId: string) => {
      if (activeId === overId) return;

      // Track user activity
      trackActivity();

      // Convert string IDs to numbers for comparison
      const activeSectionId = Number(activeId);
      const overSectionId = Number(overId);

      const updatedTemplate = {
        ...templateEditable,
        templateSections: (() => {
          const oldIndex = templateEditable.templateSections.findIndex((s) => s.id === activeSectionId);
          const newIndex = templateEditable.templateSections.findIndex((s) => s.id === overSectionId);

          const newSections = [...templateEditable.templateSections];
          const [movedSection] = newSections.splice(oldIndex, 1);
          newSections.splice(newIndex, 0, movedSection);
          return newSections;
        })(),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // DO NOT trigger auto-save for section reordering
      // Section reordering is only saved when clicking "Done"
    },
    [templateEditable, trackActivity]
  );

  // Handle section reordering with drop zones
  const handleSectionInsertAtIndex = useCallback(
    (activeId: string, insertIndex: number) => {
      trackActivity();

      // Convert string ID to number for comparison
      const activeSectionId = Number(activeId);

      const oldIndex = templateEditable.templateSections.findIndex((s) => s.id === activeSectionId);
      if (oldIndex === -1) return;

      if (oldIndex === insertIndex || oldIndex === insertIndex - 1) return;

      const updatedTemplate = {
        ...templateEditable,
        templateSections: (() => {
          const newSections = [...templateEditable.templateSections];
          const [movedSection] = newSections.splice(oldIndex, 1);

          // Adjust insert index if we're moving a section from before the target position
          const adjustedIndex = oldIndex < insertIndex ? insertIndex - 1 : insertIndex;
          newSections.splice(adjustedIndex, 0, movedSection);

          return newSections;
        })(),
      };
      setTemplateEditable(updatedTemplate);
    },
    [templateEditable, trackActivity]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !dragData) {
        setActiveId(null);
        setDragData(null);
        return;
      }

      const overId = over.id as string;
      const overData = over.data.current as any;

      // Handle different drag scenarios
      if (dragData.type === 'field-from-bank') {
        // Adding field from bank to a section
        if (overData?.type === 'section-drop-zone') {
          const sectionId = overData.sectionId as number;
          const sectionGuid = overData.sectionGuid as string;

          // Pass both id and guid to handle sections with id: 0
          addFieldToSection(dragData.field!, sectionId, sectionGuid).catch((error) => {
            console.error('Error adding field to section:', error);
          });
        } else if (overData?.type === 'section-insert-zone') {
          // Adding field from bank to a specific position in section
          const sectionId = overData.sectionId as number;
          const sectionGuid = overData.sectionGuid as string;
          const insertIndex = overData.insertIndex as number;

          // Pass both id and guid to handle sections with id: 0
          addFieldToSectionAtIndex(dragData.field!, sectionId, insertIndex, sectionGuid).catch((error) => {
            console.error('Error adding field to section at index:', error);
          });
        }
      } else if (dragData.type === 'field-within-section') {
        // Check if dropping on insert zone for repositioning
        if (overData?.type === 'section-insert-zone') {
          const sectionId = overData.sectionId as number;
          const sectionGuid = overData.sectionGuid as string;
          const insertIndex = overData.insertIndex as number;

          // Pass both id and guid to handle sections with id: 0
          handleFieldInsertAtIndex(active.id as string, sectionId, insertIndex, dragData, sectionGuid).catch(
            (error) => {
              console.error('Error moving field to specific position:', error);
            }
          );
        } else if (overData?.type === 'section-drop-zone') {
          // Moving field from one section to an empty section
          const sectionId = overData.sectionId as number;
          const sectionGuid = overData.sectionGuid as string;

          // Pass both id and guid to handle sections with id: 0
          handleFieldInsertAtIndex(active.id as string, sectionId, 0, dragData, sectionGuid).catch((error) => {
            console.error('Error moving field to empty section:', error);
          });
        } else {
          // Reordering fields within sections (existing functionality)
          handleFieldReorder(active.id as string, overId, dragData).catch((error) => {
            console.error('Error reordering fields within section:', error);
          });
        }
      } else if (dragData.type === 'section' && isReorderMode) {
        // Check if dropping on section reorder zone for repositioning
        if (overData?.type === 'section-reorder-zone') {
          const insertIndex = overData.insertIndex as number;
          handleSectionInsertAtIndex(active.id as string, insertIndex);
        } else {
          // Reordering sections (existing functionality)
          handleSectionReorder(active.id as string, overId);
        }
      }

      setActiveId(null);
      setDragData(null);
      setIsOverDropZone(false);
    },
    [
      dragData,
      isReorderMode,
      addFieldToSection,
      addFieldToSectionAtIndex,
      handleFieldInsertAtIndex,
      handleFieldReorder,
      handleSectionReorder,
      handleSectionInsertAtIndex,
    ]
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    // Track if we're currently over a valid drop zone
    setIsOverDropZone(!!over);
  }, []);

  return {
    activeId,
    dragData,
    isOverDropZone,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
  };
};
